import { createContext, SetStateAction, useContext, useState } from "react";
import { GetSearchConditionsGetResponseModel } from "@/features/accounts/shared/services/get-search-conditions-model";

export interface Detail {
  mlinkAccountId: string;
  mlinkId: string;
  tradeId: string;
  date: string;
  sortOrder: number; // 同一日付内順序
  tradeType: number; // 1:入金, 2:出金
  amount: number;
  balance: number | undefined;
  summary: string;
  memo: string;
  invalid: number; // 0:有効明細, 1:無効明細
  orderId: string; // ECショップ用注文ID
  cancelOrder: number; // ECショップ用キャンセル受注(0：通常受注、1：キャンセル受注)
  isHeader: number; // ECショップ用明細ヘッダー行フラグ(0：通常行、1：ヘッダー行)
  isEcshopTrade: number; // ECショップ明細フラグ(0：通常明細、1：ECショップ明細)
  ocrProcessId: string; // OCR口座用OCR処理ID
}

export interface TradesHistory {
  mlinkAccountId: string;
  mlinkId: string;
  data: HistoryInfo[];
  hasMore: boolean;
}

export interface HistoryInfo {
// export interface TradesHistory {
    executedAt: string //YYYY-MM-DD hh:mm:ss (ISOformat)
    updateType: number // 0:自動、1:手動
    status: number // 0:成功 1:エラー 2:スキップ 3:警告
    user: string
    details: string
}

export interface SearchCondition extends GetSearchConditionsGetResponseModel {
  isSelected: boolean;
}

interface TradesContextType {
  openTrades: boolean;
  details: Detail[];
  selectedDetails: Detail[];
  sortType: "asc" | "desc";
  completeFirstFetch: boolean;
  tradesStartDate: string;
  tradesEndDate: string;
  searchConditionsList: SearchCondition[];
  selectedConditionId: string | undefined;
  searchTradeType: 0 | 1 | 2; // 0:全て, 1:入金, 2:出金
  checkDeposit: boolean;
  checkWithdrawal: boolean;
  checkNoSpec: boolean;
  searchStartDate: string;
  searchEndDate: string;
  searchMinAmount: number | undefined;
  searchMaxAmount: number | undefined;
  searchMinBalance: number | undefined;
  searchMaxBalance: number | undefined;
  searchSummaryKeyword: string;
  searchMemoKeyword: string;
  activeInvalid: boolean;
  activeBalance: boolean;
  startDateForce: string | undefined; // 明細取得期間の開始日を強制的に指定する（主に更新後の再取得用）
  endDateForce: string | undefined;   // 明細取得期間の終了日を強制的に指定する（主に更新後の再取得用）
  forceTradesSetDone: boolean;
  setOpenTrades: React.Dispatch<SetStateAction<boolean>>;
  setDetails: React.Dispatch<SetStateAction<Detail[]>>;
  setSelectedDetails: React.Dispatch<SetStateAction<Detail[]>>;
  setSortType: React.Dispatch<SetStateAction<"asc" | "desc">>;
  setCompleteFirstFetch: React.Dispatch<SetStateAction<boolean>>;
  setTradesStartDate: React.Dispatch<SetStateAction<string>>;
  setTradesEndDate: React.Dispatch<SetStateAction<string>>;
  setSearchConditionsList: React.Dispatch<SetStateAction<SearchCondition[]>>;
  setSelectedConditionId: React.Dispatch<SetStateAction<string | undefined>>;
  setSearchTradeType: React.Dispatch<SetStateAction<0 | 1 | 2>>; // 0:全て, 1:入金, 2:出金
  setCheckDeposit: React.Dispatch<SetStateAction<boolean>>;
  setCheckWithdrawal: React.Dispatch<SetStateAction<boolean>>;
  setCheckNoSpec: React.Dispatch<SetStateAction<boolean>>;
  setSearchStartDate: React.Dispatch<SetStateAction<string>>;
  setSearchEndDate: React.Dispatch<SetStateAction<string>>;
  setSearchMinAmount: React.Dispatch<SetStateAction<number | undefined>>;
  setSearchMaxAmount: React.Dispatch<SetStateAction<number | undefined>>;
  setSearchMinBalance: React.Dispatch<SetStateAction<number | undefined>>;
  setSearchMaxBalance: React.Dispatch<SetStateAction<number | undefined>>;
  setSearchSummaryKeyword: React.Dispatch<SetStateAction<string>>;
  setSearchMemoKeyword: React.Dispatch<SetStateAction<string>>;
  setActiveInvalid: React.Dispatch<SetStateAction<boolean>>;
  setActiveBalance: React.Dispatch<SetStateAction<boolean>>;
  setStartDateForce: React.Dispatch<SetStateAction<string | undefined>>;
  setEndDateForce: React.Dispatch<SetStateAction<string | undefined>>;
  setForceTradesSetDone: React.Dispatch<SetStateAction<boolean>>;
  clearConditionalSearch: () => void;
  resetConditionalSearch: () => void;
  resetTradesState: () => void;
}

const TradesContext = createContext<TradesContextType | undefined>(undefined);

export const TradesProvider = ({children}: {children: React.ReactNode}) => {
  const [openTrades, setOpenTrades] = useState<boolean>(false);
  const [details, setDetails] = useState<Detail[]>([]);
  const [selectedDetails, setSelectedDetails] = useState<Detail[]>(details);
  // 昇順降順
  const [sortType, setSortType] = useState<"asc" | "desc">("desc");
  // 初回取得フラグ
  const [completeFirstFetch, setCompleteFirstFetch] = useState<boolean>(false);
  // 取得期間
  const [tradesStartDate, setTradesStartDate] = useState<string>("");
  const [tradesEndDate, setTradesEndDate] = useState<string>("");

  // 検索 条件リスト
  const [searchConditionsList, setSearchConditionsList] = useState<SearchCondition[]>([]);
  const [selectedConditionId, setSelectedConditionId] = useState<string | undefined>(undefined);
  // 検索 入出金タイプ
  const [searchTradeType, setSearchTradeType] = useState<0 | 1 | 2>(0); // 0:全て, 1:入金, 2:出金
  const [checkDeposit, setCheckDeposit] = useState(false); // 入金
  const [checkWithdrawal, setCheckWithdrawal] = useState(false); // 出金
  const [checkNoSpec, setCheckNoSpec] = useState(true);  // 指定なし
  // 検索 日付
  const [searchStartDate, setSearchStartDate] = useState<string>("");
  const [searchEndDate, setSearchEndDate] = useState<string>("");
  // 検索 入出金額
  const [searchMinAmount, setSearchMinAmount] = useState<number | undefined>(undefined);
  const [searchMaxAmount, setSearchMaxAmount] = useState<number | undefined>(undefined);
  // 検索 残高
  const [searchMinBalance, setSearchMinBalance] = useState<number | undefined>(undefined);
  const [searchMaxBalance, setSearchMaxBalance] = useState<number | undefined>(undefined);
  // 検索 摘要
  const [searchSummaryKeyword, setSearchSummaryKeyword] = useState<string>("");
  // 検索 メモ
  const [searchMemoKeyword, setSearchMemoKeyword] = useState<string>("");

  // 無効明細設定用タプルボタン列表示管理
  const [activeInvalid, setActiveInvalid] = useState<boolean>(false);
  // 残高列表示管理
  const [activeBalance, setActiveBalance] = useState<boolean>(true);
  // 明細更新実行のフラグ
  const [forceTradesSetDone, setForceTradesSetDone] = useState<boolean>(false);
  // 明細取得期間の強制指定
  const [startDateForce, setStartDateForce] = useState<string | undefined>(undefined);
  const [endDateForce, setEndDateForce] = useState<string | undefined>(undefined);

  const clearConditionalSearch = () => {
    setSelectedConditionId(undefined);
    setSearchTradeType(0); // 0:全て, 1:入金, 2:出金
    setCheckDeposit(false);
    setCheckWithdrawal(false);
    setCheckNoSpec(true);
    setSearchStartDate("");
    setSearchEndDate("");
    setSearchMinAmount(undefined);
    setSearchMaxAmount(undefined);
    setSearchMinBalance(undefined);
    setSearchMaxBalance(undefined);
    setSearchSummaryKeyword("");
    setSearchMemoKeyword("");
  };

  const resetConditionalSearch = () => {
    setSearchConditionsList([]);
    clearConditionalSearch();
  };

  const resetTradesState = () => {
    setOpenTrades(false);
    setDetails([]);
    setSelectedDetails([]);
    setCompleteFirstFetch(false);
    setTradesStartDate("");
    setTradesEndDate("");
    setStartDateForce(undefined);
    setEndDateForce(undefined);
    setForceTradesSetDone(false);
    resetConditionalSearch();
  };

  return (
    <TradesContext.Provider
      value={{
        openTrades,
        details,
        selectedDetails,
        sortType,
        completeFirstFetch,
        tradesStartDate,
        tradesEndDate,
        searchConditionsList,
        selectedConditionId,
        searchTradeType,
        checkDeposit,
        checkWithdrawal,
        checkNoSpec,
        searchStartDate,
        searchEndDate,
        searchMinAmount,
        searchMaxAmount,
        searchMinBalance,
        searchMaxBalance,
        searchSummaryKeyword,
        searchMemoKeyword,
        activeInvalid,
        activeBalance,
        startDateForce,
        endDateForce,
        forceTradesSetDone,
        setOpenTrades,
        setDetails,
        setSelectedDetails,
        setSortType,
        setCompleteFirstFetch,
        setTradesStartDate,
        setTradesEndDate,
        setSearchConditionsList,
        setSelectedConditionId,
        setSearchTradeType,
        setCheckDeposit,
        setCheckWithdrawal,
        setCheckNoSpec,
        setSearchStartDate,
        setSearchEndDate,
        setSearchMinAmount,
        setSearchMaxAmount,
        setSearchMinBalance,
        setSearchMaxBalance,
        setSearchSummaryKeyword,
        setSearchMemoKeyword,
        setActiveInvalid,
        setActiveBalance,
        setStartDateForce,
        setEndDateForce,
        setForceTradesSetDone,
        clearConditionalSearch,
        resetConditionalSearch,
        resetTradesState,
      }}
    >
      {children}
    </TradesContext.Provider>
  );
};

export const useTradesContext = () => {
  const context = useContext(TradesContext);
  if (!context) throw new Error("TradesContext must be used within TradesProvider");
  return context;
};